import { useState, useCallback, useMemo, useEffect, useRef } from 'react';

export type SelectMode = 'none' | 'some' | 'all';

export interface UseBulkSelectionOptions {
  maxSelection?: number;
  onSelectionChange?: (selected: Set<number>) => void;
}

export interface UseBulkSelectionReturn {
  selectedIds: Set<number>;
  selectMode: SelectMode;
  toggleSelection: (id: number, event?: React.MouseEvent) => void;
  selectAll: (ids: number[]) => void;
  clearSelection: () => void;
  isSelected: (id: number) => boolean;
  selectedCount: number;
  canSelect: boolean;
}

/**
 * Hook for managing bulk selection state with support for
 * individual selection, select all, range selection (shift-click),
 * and keyboard shortcuts.
 */
export const useBulkSelection = (
  options: UseBulkSelectionOptions = {}
): UseBulkSelectionReturn => {
  const { maxSelection = 1000, onSelectionChange } = options;

  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const lastSelectedRef = useRef<number | null>(null);

  // Calculate select mode
  const selectMode: SelectMode = useMemo(() => {
    if (selectedIds.size === 0) return 'none';
    return 'some';
  }, [selectedIds.size]);

  // Toggle individual item selection
  const toggleSelection = useCallback((id: number, event?: React.MouseEvent) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      
      if (newSet.has(id)) {
        newSet.delete(id);
        lastSelectedRef.current = null;
      } else {
        // Check max selection limit
        if (newSet.size >= maxSelection) {
          console.warn(`Maximum selection limit of ${maxSelection} reached`);
          return prev;
        }
        newSet.add(id);
        lastSelectedRef.current = id;
      }
      
      return newSet;
    });
  }, [maxSelection]);

  // Select all items
  const selectAll = useCallback((ids: number[]) => {
    const limitedIds = ids.slice(0, maxSelection);
    setSelectedIds(new Set(limitedIds));
    lastSelectedRef.current = null;
  }, [maxSelection]);

  // Clear all selections
  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    lastSelectedRef.current = null;
  }, []);

  // Check if an item is selected
  const isSelected = useCallback((id: number): boolean => {
    return selectedIds.has(id);
  }, [selectedIds]);

  // Notify on selection change
  useEffect(() => {
    onSelectionChange?.(selectedIds);
  }, [selectedIds, onSelectionChange]);

  return {
    selectedIds,
    selectMode,
    toggleSelection,
    selectAll,
    clearSelection,
    isSelected,
    selectedCount: selectedIds.size,
    canSelect: selectedIds.size < maxSelection
  };
};

/**
 * Hook for shift-click range selection
 */
export const useRangeSelection = <T extends { id: number }>(
  items: T[],
  onRangeSelect: (ids: number[]) => void
) => {
  const lastSelectedIndexRef = useRef<number | null>(null);

  const handleClick = useCallback((index: number, event: React.MouseEvent) => {
    if (event.shiftKey && lastSelectedIndexRef.current !== null) {
      const start = Math.min(lastSelectedIndexRef.current, index);
      const end = Math.max(lastSelectedIndexRef.current, index);
      const rangeIds = items.slice(start, end + 1).map(item => item.id);
      onRangeSelect(rangeIds);
    }
    lastSelectedIndexRef.current = index;
  }, [items, onRangeSelect]);

  return { handleRangeClick: handleClick };
};
